// crewbit-admin/src/components/schedule/RouteMap.tsx
//
// Schedule Module Phase C C4 — admin route map. Wraps mapbox-gl Map +
// Polyline GeoJSON layer + custom HTML markers. Pattern mirrors
// crewbit-admin/src/pages/Dispatch/DispatchMap.tsx (existing mapbox-gl consumer).
//
// Token: VITE_MAPBOX_TOKEN env var per DispatchMap.tsx precedent. Graceful
// fallback to "set token" placeholder when missing.
//
// Spec: docs/references/schedule/SCHEDULE_MODULE_SPEC_v1.md §5.4 + §5.5.

import { useEffect, useRef, useState } from 'react';
import { renderToString } from 'react-dom/server';
import { palette, admin } from '../../styles/tokens';
import type { ScheduleEntry } from '../../types/schedule';
import { StopMarker } from './StopMarker';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined;

export interface RouteMapProps {
  stops: ScheduleEntry[];
  onStopTap: (entryId: string) => void;
}

interface StopCoord {
  entryId: string;
  lat: number;
  lng: number;
  status: string;
  index: number;
}

function deriveStopCoords(stops: ScheduleEntry[]): StopCoord[] {
  const out: StopCoord[] = [];
  let idx = 1;
  for (const s of stops) {
    if (s.location_lat == null || s.location_lng == null) continue;
    out.push({
      entryId: s.id,
      lat: s.location_lat,
      lng: s.location_lng,
      status: s.status,
      index: idx++,
    });
  }
  return out;
}

export function RouteMap({ stops, onStopTap }: RouteMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const markersRef = useRef<any[]>([]);
  const [mapError, setMapError] = useState<string | null>(null);
  const [mapReady, setMapReady] = useState(false);

  // Init map (once)
  useEffect(() => {
    if (!MAPBOX_TOKEN) {
      setMapError('Set VITE_MAPBOX_TOKEN in .env to enable the route map');
      return;
    }
    if (!containerRef.current || mapRef.current) return;

    let cancelled = false;
    void (async () => {
      const mapboxgl = (await import('mapbox-gl')).default;
      await import('mapbox-gl/dist/mapbox-gl.css');
      if (cancelled || !containerRef.current) return;

      const map = new mapboxgl.Map({
        container: containerRef.current,
        accessToken: MAPBOX_TOKEN!,
        style: 'mapbox://styles/mapbox/light-v11',
        center: [-79.38, 43.65],
        zoom: 11,
        cooperativeGestures: true,
      });
      map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
      mapRef.current = map;
      map.on('load', () => { if (!cancelled) setMapReady(true); });
    })().catch(() => setMapError('Failed to load map'));

    return () => {
      cancelled = true;
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
  }, []);

  // Sync markers + polyline whenever stops change.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const coords = deriveStopCoords(stops);

    void (async () => {
      const mapboxgl = (await import('mapbox-gl')).default;

      for (const m of markersRef.current) m.remove();
      markersRef.current = [];

      if (coords.length === 0) return;

      const bounds = new mapboxgl.LngLatBounds();
      for (const c of coords) {
        const el = document.createElement('div');
        el.innerHTML = renderToString(
          <StopMarker index={c.index} status={c.status} density={coords.length >= 10 ? 'compact' : 'normal'} />,
        );
        el.style.cursor = 'pointer';
        el.addEventListener('click', () => onStopTap(c.entryId));
        const marker = new mapboxgl.Marker({ element: el }).setLngLat([c.lng, c.lat]).addTo(map);
        markersRef.current.push(marker);
        bounds.extend([c.lng, c.lat]);
      }

      // Polyline as GeoJSON line layer (re-create on each sync)
      const layerId = 'route-polyline';
      const sourceId = 'route-polyline-src';
      if (map.getLayer(layerId)) map.removeLayer(layerId);
      if (map.getSource(sourceId)) map.removeSource(sourceId);
      if (coords.length > 1) {
        map.addSource(sourceId, {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: { type: 'LineString', coordinates: coords.map((c) => [c.lng, c.lat]) },
          },
        });
        map.addLayer({
          id: layerId,
          type: 'line',
          source: sourceId,
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: { 'line-color': palette.accent, 'line-width': 3 },
        });
      }

      if (coords.length === 1) {
        map.flyTo({ center: [coords[0].lng, coords[0].lat], zoom: 13, essential: true });
      } else {
        map.fitBounds(bounds, { padding: 48, maxZoom: 14, duration: 600 });
      }
    })();
  }, [stops, mapReady, onStopTap]);

  if (mapError) {
    return (
      <div style={{
        flex: 1, padding: 32, textAlign: 'center',
        backgroundColor: admin.bg, color: palette.g500, fontSize: 13,
      }}>
        {mapError}
      </div>
    );
  }

  if (deriveStopCoords(stops).length === 0) {
    return (
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: 32, backgroundColor: admin.bg, gap: 8,
      }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: palette.g700 }}>No mappable stops</div>
        <div style={{ fontSize: 12, color: palette.g500 }}>Stops need a location to render on the map.</div>
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ flex: 1, minHeight: 320, width: '100%' }} />
  );
}
